import type { CSSProperties } from 'react'
import './Icon.css'
import { cx } from '@/utils/cx'
import type { IconType } from './iconTypes'

import { ArrowDown } from './icons/wolfround/arrowDown'
import { ArrowLeft } from './icons/wolfround/arrowLeft'
import { ArrowRight } from './icons/wolfround/arrowRight'
import { ArrowUp } from './icons/wolfround/arrowUp'
import { Bank } from './icons/wolfround/bank'
import { Calendar } from './icons/wolfround/calendar'
import { Camera } from './icons/wolfround/camera'
import { CaretDown } from './icons/wolfround/caretDown'
import { CaretLeft } from './icons/wolfround/caretLeft'
import { CaretRight } from './icons/wolfround/caretRight'
import { CaretUp } from './icons/wolfround/caretUp'
import { Change } from './icons/wolfround/change'
import { Check } from './icons/wolfround/check'
import { CheckCircle } from './icons/wolfround/checkCircle'
import { DeviceDesktop } from './icons/wolfround/deviceDesktop'
import { DeviceMobile } from './icons/wolfround/deviceMobile'
import { Document } from './icons/wolfround/document'
import { DotsHorizontal } from './icons/wolfround/dotsHorizontal'
import { Download } from './icons/wolfround/download'
import { DrivingLicense } from './icons/wolfround/drivingLicense'
import { Edit } from './icons/wolfround/edit'
import { Email } from './icons/wolfround/email'
import { Euro } from './icons/wolfround/euro'
import { ExternalLink } from './icons/wolfround/externalLink'
import { Fullscreen } from './icons/wolfround/fullscreen'
import { Globe } from './icons/wolfround/globe'
import { Id } from './icons/wolfround/id'
import { IdentityCard } from './icons/wolfround/identityCard'
import { Info } from './icons/wolfround/info'
import { Letter } from './icons/wolfround/letter'
import { Lightbulb } from './icons/wolfround/lightbulb'
import { Micropayment } from './icons/wolfround/micropayment'
import { Mismatch } from './icons/wolfround/mismatch'
import { Monitor } from './icons/wolfround/monitor'
import { Passkey } from './icons/wolfround/passkey'
import { Passport } from './icons/wolfround/passport'
import { PictureId } from './icons/wolfround/pictureId'
import { BankIdLogo } from '@/components/BankIdLogo'
import { Printer } from './icons/wolfround/printer'
import { Refresh } from './icons/wolfround/refresh'
import { ResidencePermit } from './icons/wolfround/residencePermit'
import { Search } from './icons/wolfround/search'
import { SelectedDocument } from './icons/wolfround/selectedDocument'
import { Smile } from './icons/wolfround/smile'
import { SocietyStatement } from './icons/wolfround/societyStatement'
import { Trash } from './icons/wolfround/trash'
import { Upload } from './icons/wolfround/upload'
import { User } from './icons/wolfround/user'
import { UtilityBill } from './icons/wolfround/utilityBill'
import { Warning } from './icons/wolfround/warning'
import { Xmark } from './icons/wolfround/xmark'
import { XmarkCircle } from './icons/wolfround/xmarkCircle'
import { ZoomIn } from './icons/wolfround/zoomIn'
import { ZoomOut } from './icons/wolfround/zoomOut'
import { Building } from './icons/wolfround/building'
import { Home } from './icons/wolfround/home'
import { Draw } from './icons/wolfround/draw'
import { LightMode } from './icons/wolfround/lightMode'
import { RolesUser } from './icons/wolfround/rolesUser'
import { UploadFile } from './icons/wolfround/uploadFile'
import { Plus } from './icons/wolfround/plus'
import { QuestionmarkCircle } from './icons/wolfround/questionmarkCircle'

export interface IconProps {
  size?: string
  type: IconType
  color?: string
  colorHover?: string
  visible?: boolean
}

export const Icon = ({ size, type, color, colorHover, visible = true }: IconProps) => {
  function renderIcon() {
    switch (type) {
      case 'arrow-down':
        return <ArrowDown color={color} />
      case 'arrow-left':
        return <ArrowLeft color={color} />
      case 'arrow-right':
        return <ArrowRight color={color} />
      case 'arrow-up':
        return <ArrowUp color={color} />
      case 'bank':
        return <Bank color={color} />
      case 'bank-id':
        return <BankIdLogo color={color} />
      case 'bulb':
        return <Lightbulb color={color} />
      case 'building':
        return <Building color={color} />
      case 'calendar':
        return <Calendar color={color} />
      case 'camera':
        return <Camera color={color} />
      case 'caret-down':
        return <CaretDown color={color} />
      case 'caret-left':
        return <CaretLeft color={color} />
      case 'caret-right':
        return <CaretRight color={color} />
      case 'caret-up':
        return <CaretUp color={color} />
      case 'change':
        return <Change color={color} />
      case 'check':
        return <Check color={color} />
      case 'check-circle':
        return <CheckCircle color={color} />
      case 'device-desktop':
        return <DeviceDesktop color={color} />
      case 'device-mobile':
        return <DeviceMobile color={color} />
      case 'document':
        return <Document color={color} />
      case 'dots-horizontal':
        return <DotsHorizontal color={color} />
      case 'download':
        return <Download color={color} />
      case 'draw':
        return <Draw color={color} />
      case 'driving-license':
        return <DrivingLicense color={color} />
      case 'edit':
        return <Edit color={color} />
      case 'email':
        return <Email color={color} />
      case 'emoji-smile':
        return <Smile color={color} />
      case 'euro':
        return <Euro color={color} />
      case 'external-link':
        return <ExternalLink color={color} />
      case 'fullscreen':
        return <Fullscreen color={color} />
      case 'globe':
        return <Globe color={color} />
      case 'home':
        return <Home color={color} />
      case 'id':
        return <Id color={color} />
      case 'identity-card':
        return <IdentityCard color={color} />
      case 'info':
        return <Info color={color} />
      case 'letter':
        return <Letter color={color} />
      case 'lightbulb':
        return <Lightbulb color={color} />
      case 'light-mode':
        return <LightMode color={color} />
      case 'micropayment':
        return <Micropayment color={color} />
      case 'mismatch':
        return <Mismatch color={color} />
      case 'monitor':
        return <Monitor color={color} />
      case 'passkey':
        return <Passkey color={color} />
      case 'passport':
        return <Passport color={color} />
      case 'picture-id':
        return <PictureId color={color} />
      case 'plus':
        return <Plus color={color} />
      case 'printer':
        return <Printer color={color} />
      case 'refresh':
        return <Refresh color={color} />
      case 'residence-permit':
        return <ResidencePermit color={color} />
      case 'roles-user':
        return <RolesUser color={color} />
      case 'search':
        return <Search color={color} />
      case 'selected-document':
        return <SelectedDocument color={color} />
      case 'society-statement':
        return <SocietyStatement color={color} />
      case 'questionmark-circle':
        return <QuestionmarkCircle color={color} />
      case 'trash':
        return <Trash color={color} />
      case 'upload':
        return <Upload color={color} />
      case 'upload-file':
        return <UploadFile color={color} />
      case 'user':
        return <User color={color} />
      case 'utility-bill':
        return <UtilityBill color={color} />
      case 'warning':
        return <Warning color={color} />
      case 'x-mark':
        return <Xmark color={color} />
      case 'x-mark-circle':
        return <XmarkCircle color={color} />
      case 'zoom-in':
        return <ZoomIn color={color} />
      case 'zoom-out':
        return <ZoomOut color={color} />
      default:
        return null
    }
  }

  return (
    <span
      className={cx('icon__container', color && 'icon__colorized', !visible && 'icon__hidden')}
      style={
        {
          '--icon-size': size || '18px',
          '--icon-color': color || undefined,
          '--icon-color-hover': colorHover || undefined,
        } as CSSProperties
      }
    >
      {renderIcon()}
    </span>
  )
}
